/**
 * The three.js scene: globe mesh, atmosphere, deep-space backdrop, camera and
 * the render loop.
 *
 * The camera is driven by a target/current pair (`cam.tLat` vs `cam.lat`) that
 * the loop eases every frame, so anything that wants to move the view sets a
 * target and lets the loop get there. Auto-rotation and the drag flag live here
 * too, because the loop is the only thing that reads them every frame.
 */
import * as THREE from 'three';
import { C, tierOf } from '../model.js';
import { hash } from '../rng.js';
import { reduceMotion } from '../env.js';
import { texCanvas, attachTexture, metricOf, selectedId } from './texture.js';

/* ================= three.js scene ================= */
let renderer, scene, camera, globeMesh;
export const cam = { lat:22, lon:12, dist:2.95, tLat:22, tLon:12, tDist:2.95 };
let autoRotate = !reduceMotion;
const beacons=[];

export function dirOf(lat,lon){
  const th=(90-lat)*Math.PI/180, ph=(lon+180)/360*2*Math.PI;
  return new THREE.Vector3(-Math.cos(ph)*Math.sin(th), Math.cos(th), Math.sin(ph)*Math.sin(th));
}
export function lonLatOf(p){
  const r=p.length();
  const lat=90 - Math.acos(p.y/r)*180/Math.PI;
  let ph=Math.atan2(p.z,-p.x); if(ph<0) ph+=Math.PI*2;
  return [lat, ph/(2*Math.PI)*360-180];
}

/* procedural deep-space backdrop: milky-way band + nebulae, drawn once */
function makeSpaceTexture(){
  const W=2048, H=1024, cv=document.createElement('canvas'); cv.width=W; cv.height=H;
  const x=cv.getContext('2d'); const rng=hash('deepspace');
  x.fillStyle='#05070C'; x.fillRect(0,0,W,H);
  const bandY=u=> H*.5 + Math.sin(u*Math.PI*2*1.15+1.2)*H*.10;
  /* soft nebulae along the band: indigo, teal, rare warm */
  for(let i=0;i<30;i++){
    const u=rng(), cx0=u*W, cy0=bandY(u)+(rng()-.5)*H*.2, r=(0.05+rng()*.12)*W;
    const col = rng()<.18 ? '107,84,51' : (rng()<.55 ? '42,59,102' : '30,74,86');
    const g=x.createRadialGradient(cx0,cy0,0,cx0,cy0,r);
    g.addColorStop(0,`rgba(${col},${.09+rng()*.10})`); g.addColorStop(1,`rgba(${col},0)`);
    x.fillStyle=g; x.beginPath(); x.arc(cx0,cy0,r,0,7); x.fill();
    if(cx0<W*.2){ x.beginPath(); x.arc(cx0+W,cy0,r,0,7); x.fill() }     /* seam wrap */
    if(cx0>W*.8){ x.beginPath(); x.arc(cx0-W,cy0,r,0,7); x.fill() }
  }
  /* dense star dust in the band */
  for(let i=0;i<9000;i++){
    const u=rng(), spread=H*.075*(rng()+rng()+rng()-1.5);
    const s=rng()<.985 ? rng()*.9+.3 : rng()*1.6+1;
    x.globalAlpha=.05+rng()*.5;
    x.fillStyle=rng()<.12 ? '#E6CB90' : '#C9D6EE';
    x.fillRect(u*W, bandY(u)+spread, s, s);
  }
  /* sparse field stars everywhere */
  for(let i=0;i<1700;i++){
    x.globalAlpha=.05+rng()*.32;
    x.fillStyle=rng()<.1 ? '#E6CB90' : '#AEBDD9';
    x.fillRect(rng()*W, rng()*H, rng()*.9+.3, rng()*.9+.3);
  }
  x.globalAlpha=1;
  const t=new THREE.CanvasTexture(cv); t.anisotropy=4; return t;
}
let bgSphere=null, beaconGroup=null, ringTex=null;
export function buildBeacons(){
  if(reduceMotion || !scene) return;
  if(beaconGroup){ scene.remove(beaconGroup) }
  beacons.length=0;
  ringTex = ringTex || ringSprite('#C8A96A');
  beaconGroup = new THREE.Group();
  C.filter(c=>c.ll && tierOf(metricOf(c))===3).forEach((c,i)=>{
    const p=dirOf(c.ll[0],c.ll[1]).multiplyScalar(1.008);
    const sp=new THREE.Sprite(new THREE.SpriteMaterial({map:ringTex, transparent:true, opacity:.5, depthWrite:false}));
    sp.position.copy(p); sp.scale.setScalar(.05); beaconGroup.add(sp);
    beacons.push({sp, ph:i*1.1});
  });
  scene.add(beaconGroup);
}

function ringSprite(color){
  const s=128, cv=document.createElement('canvas'); cv.width=cv.height=s;
  const x=cv.getContext('2d');
  x.strokeStyle=color; x.lineWidth=5; x.beginPath(); x.arc(s/2,s/2,s/2-6,0,7); x.stroke();
  return new THREE.CanvasTexture(cv);
}
function dotSprite(color){
  const s=64, cv=document.createElement('canvas'); cv.width=cv.height=s;
  const x=cv.getContext('2d'); const g=x.createRadialGradient(s/2,s/2,0,s/2,s/2,s/2);
  g.addColorStop(0,color); g.addColorStop(.5,color+'CC'); g.addColorStop(1,color+'00');
  x.fillStyle=g; x.fillRect(0,0,s,s);
  return new THREE.CanvasTexture(cv);
}

export function initThree(){
  const cv=document.getElementById('globe');
  renderer = new THREE.WebGLRenderer({canvas:cv, antialias:true, alpha:true});
  renderer.setPixelRatio(Math.min(1.75, devicePixelRatio));
  renderer.setSize(innerWidth, innerHeight);
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(38, innerWidth/innerHeight, .1, 100);

  const texture = new THREE.CanvasTexture(texCanvas);
  texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  attachTexture(texture);
  const mat = new THREE.MeshPhongMaterial({map:texture, shininess:6, specular:new THREE.Color('#1A2432')});
  globeMesh = new THREE.Mesh(new THREE.SphereGeometry(1, 96, 96), mat);
  scene.add(globeMesh);

  /* atmosphere */
  const atm = new THREE.Mesh(new THREE.SphereGeometry(1.13, 64, 64), new THREE.ShaderMaterial({
    vertexShader:`varying vec3 vN; void main(){ vN=normalize(normalMatrix*normal); gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader:`varying vec3 vN; void main(){ float i=pow(0.64-dot(vN,vec3(0.,0.,1.)),4.2); gl_FragColor=vec4(0.34,0.5,0.78,1.0)*i + vec4(0.55,0.42,0.2,1.0)*pow(i,2.4)*.5; }`,
    blending:THREE.AdditiveBlending, side:THREE.BackSide, transparent:true, depthWrite:false
  }));
  scene.add(atm);

  scene.add(new THREE.AmbientLight(0xffffff, .72));
  const sun = new THREE.DirectionalLight(0xffe7bd, .62); sun.position.set(-3,2,3); scene.add(sun);
  const fill = new THREE.DirectionalLight(0x9FC0FF, .24); fill.position.set(3,-1,-2); scene.add(fill);

  /* stars */
  /* deep-space backdrop sphere, tilted like the galactic plane */
  bgSphere = new THREE.Mesh(new THREE.SphereGeometry(72, 48, 48),
    new THREE.MeshBasicMaterial({map:makeSpaceTexture(), side:THREE.BackSide, depthWrite:false}));
  bgSphere.rotation.z = .5; bgSphere.rotation.x = .12;
  scene.add(bgSphere);
  /* a distant sun — soft warm glow behind the scene */
  const sunGlow = new THREE.Sprite(new THREE.SpriteMaterial({map:dotSprite('#F3E3BC'),
    transparent:true, opacity:.5, depthWrite:false, blending:THREE.AdditiveBlending}));
  sunGlow.position.set(-46, 14, 34); sunGlow.scale.setScalar(26);
  scene.add(sunGlow);

  const rng=hash('stars3d');
  const mkStars=(n,color,size,op)=>{
    const g=new THREE.BufferGeometry(), pos=new Float32Array(n*3);
    for(let i=0;i<n;i++){
      const v=new THREE.Vector3(rng()*2-1,rng()*2-1,rng()*2-1).normalize().multiplyScalar(28+rng()*36);
      pos[i*3]=v.x; pos[i*3+1]=v.y; pos[i*3+2]=v.z;
    }
    g.setAttribute('position', new THREE.BufferAttribute(pos,3));
    scene.add(new THREE.Points(g, new THREE.PointsMaterial({color, size, transparent:true, opacity:op, sizeAttenuation:true})));
  };
  mkStars(700, 0x93A3C0, .09, .65);
  mkStars(130, 0xE6CB90, .11, .75);   /* warm champagne stars */

  /* beacons on top-tier countries (rebuilt when the colour metric changes) */
  buildBeacons();

  addEventListener('resize', ()=>{
    camera.aspect=innerWidth/innerHeight; camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight);
  });
  requestAnimationFrame(loop);
  return cv;   // the caller binds pointer handling to it
}

/* smooth lon lerp (wrap-aware) — the scroll story interpolates camera keys with it too */
export function lerpLon(a,b,k){ let d=b-a; if(d>180)d-=360; if(d<-180)d+=360; return a+d*k }

let lastT=0;
function loop(t){
  requestAnimationFrame(loop);
  const dt=Math.min(50,t-lastT); lastT=t;
  if(autoRotate && !dragging && !selectedId()) cam.tLon += dt*.0022;
  const k = reduceMotion? 1 : Math.min(1, dt*.0042);
  cam.lat += (cam.tLat-cam.lat)*k;
  cam.lon = lerpLon(cam.lon, cam.tLon, k);
  cam.dist += (cam.tDist-cam.dist)*k;
  camera.position.copy(dirOf(cam.lat, cam.lon)).multiplyScalar(cam.dist);
  camera.lookAt(0,0,0);
  if(bgSphere && !reduceMotion) bgSphere.rotation.y += dt*.0000045;   /* imperceptible galactic drift */
  const pulse=(Math.sin(t*.0016)+1)/2;
  beacons.forEach(b=>{
    const p=(Math.sin(t*.0016+b.ph)+1)/2;
    b.sp.scale.setScalar(.035+p*.03); b.sp.material.opacity=.55-p*.4;
  });
  renderer.render(scene, camera);
}

export function flyToLL(lat,lon,dist){
  cam.tLat=lat; cam.tLon=lon;
  if(dist) cam.tDist=dist;
  if(reduceMotion){ cam.lat=cam.tLat; cam.lon=cam.tLon; cam.dist=cam.tDist }
}

let dragging = false;

/** The pointer reports drags; the loop pauses auto-rotation while one is in progress. */
export function setDragging(v) { dragging = v }
export const isDragging = () => dragging;

/** The scroll story turns auto-rotation off once the explorer is on screen. */
export function setAutoRotate(v) { autoRotate = v }

/** Screen point -> [lat, lon] on the globe, or null when the ray misses it. */
const ray = new THREE.Raycaster(), ndc = new THREE.Vector2();
export function raycastLatLon(clientX, clientY) {
  ndc.x = (clientX / innerWidth) * 2 - 1; ndc.y = -(clientY / innerHeight) * 2 + 1;
  ray.setFromCamera(ndc, camera);
  const hit = ray.intersectObject(globeMesh)[0];
  return hit ? lonLatOf(hit.point) : null;
}
